// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: LGPL-2.1-only

import type { ComponentInterface } from "@stencil/core";
import { Component, h, Host, Prop, State } from "@stencil/core";

@Component({
  tag: "qds-web-footer",
  shadow: true,
  styleUrl: "footer.css",
})
export class Footer implements ComponentInterface {
  /**
   * The year of the copyright.
   *
   * @public
   * @type {number}
   */
  @Prop() public copyrightYear: number;

  /**
   * The href for the company Logo.
   *
   * @remarks
   * This property is used to specify the URL for the web logo.
   *
   * @public
   * @type {string}
   */
  @Prop() public hrefLogo: string;

  /**
   * The src for the company Logo.
   *
   * @remarks
   * This property is used to specify the src for the web company logo.
   *
   * @public
   * @type {string}
   */
  @Prop() public srcLogo: string;

  @State() private hideCtaSlot = true;

  @State() private hideDefaultSlot = true;

  @State() private hideMethodsSlot = true;

  public render() {
    const isSmallHeight =
      this.hideMethodsSlot && this.hideCtaSlot && this.hideDefaultSlot;
    return (
      <Host
        class={{
          "qds-theme-dark": true,
        }}
      >
        <footer class={{ "small-height": isSmallHeight }}>
          <img
            loading="lazy"
            class="image"
            src="https://d2osz8slymlqdp.cloudfront.net/@quartzds/se-assets/@1.0.0-beta.2/illustrations/illustration-footer.svg"
            alt=""
            aria-hidden="true"
          />
          <div class="content">
            <slot name="subscription" />
            <div class={{ cta: true, hidden: this.hideCtaSlot }}>
              <slot name="cta" onSlotchange={this.handleSlotChange} />
              <qds-divider />
            </div>
            <div class={{ menu: true, hidden: this.hideDefaultSlot }}>
              <slot onSlotchange={this.handleSlotChange} />
            </div>
            <div class={{ methods: true, hidden: this.hideMethodsSlot }}>
              <slot name="methods" onSlotchange={this.handleSlotChange} />
            </div>
            <div class="media-logo">
              <div>
                <slot name="media" />
              </div>
              <a
                class="logo"
                href={this.hrefLogo}
                target="_blank"
                rel="noreferrer"
              >
                <img class="logo" src={this.srcLogo} alt="Impact company" />
              </a>
            </div>
            <div class="bottom-links">
              <slot name="links" />
              <p class="copyright">
                © {this.copyrightYear}{" "}
                <span lang="en">Schneider Electric</span>
              </p>
            </div>
          </div>
        </footer>
      </Host>
    );
  }

  private readonly handleSlotChange = (event: Event): void => {
    const slot = event.target as HTMLSlotElement;
    const isEmpty = slot.assignedElements().length === 0;

    if (!slot.name) {
      this.hideDefaultSlot = isEmpty;
    }

    switch (slot.name) {
      case "methods": {
        this.hideMethodsSlot = isEmpty;
        break;
      }
      case "cta": {
        this.hideCtaSlot = isEmpty;
        break;
      }
      default: {
        break;
      }
    }
  };
}
